import {
  DataType,
  FormulaOperator,
  LogicExpressionCondition,
  LogicExpressionType,
} from '@doss/prisma/types'
import {
  CharStreams,
  CommonToken,
  CommonTokenStream,
  ParseTreeWalker,
  ParserRuleContext,
  RecognitionException,
  Recognizer,
  Token,
} from 'antlr4'
import _ from 'lodash'
import {
  dataTypeToFormulaValueType,
  filteredKeywordToBaseOperator,
  inferFormulaOutputType,
  type FormulaValueType,
} from '../../utils/formula-operator-specs.js'
import {
  checkLogicExpressionOperandTypes,
  type LogicOperandType,
} from '../../utils/logic-expression-operands.js'
import {
  DossDataFormat,
  dataTypeToDossDataFormat,
  isTableCellData,
} from '../../utils/types/table-cell.js'
import FormulaLexer from '../generated/formulaLexer.js'
import formulaListener from '../generated/formulaListener.js'
import formulaParser, {
  AdditionExprContext,
  AndChainedLogicExprContext,
  ArrayRightContainsExprContext,
  ArrayRightContainsExpressionContext,
  AverageFunctionContext,
  AverageIfFunctionContext,
  BinaryLogicExprContext,
  BooleanExpressionContext,
  BooleanValueContext,
  CeilFunctionContext,
  ColumnRefContext,
  ComparisonExpressionContext,
  ConcatFunctionContext,
  ContainsAllContext,
  ContainsAnyContext,
  ContainsContext,
  CountDistinctFunctionContext,
  CountFunctionContext,
  CountIfFunctionContext,
  DateFunctionContext,
  DateaddFunctionContext,
  DatediffFunctionContext,
  DayFunctionContext,
  DirectValueContext,
  DivisionExprContext,
  DoesNotContainAnyContext,
  DoesNotContainContext,
  EqualContext,
  EvenExpressionsContext,
  ExprContext,
  FloorFunctionContext,
  FormulaInLogicContext,
  FunctionContext,
  GreaterThanContext,
  GreaterThanOrEqualContext,
  IfFunctionContext,
  IfsFunctionContext,
  InContext,
  InExprContext,
  InExpressionContext,
  IsEmptyContext,
  IsNotEmptyContext,
  LeftFunctionContext,
  LenFunctionContext,
  LessThanContext,
  LessThanOrEqualContext,
  LogicExprColumnRefContext,
  LogicExprContext,
  LowerFunctionContext,
  MaxFunctionContext,
  MaxIfsFunctionContext,
  MidFunctionContext,
  MinFunctionContext,
  MinIfsFunctionContext,
  MonthFunctionContext,
  MultipleArgumentsFunctionContext,
  MultipleExpressionsContext,
  MultiplicationExprContext,
  NegativeNumberContext,
  NegativeNumericItemContext,
  NegativeNumericValueContext,
  NestedSumExprContext,
  NotEqualContext,
  NotExprContext,
  NotExpressionContext,
  NotInContext,
  NowLiteralContext,
  NumberContext,
  NumericItemContext,
  NumericValueContext,
  NumericValueListContext,
  OrChainedLogicExprContext,
  ParenthesizedLogicExprContext,
  ParenthesizedLogicTermContext,
  RightFunctionContext,
  RoundFunctionContext,
  ScalarRightContainsExprContext,
  ScalarRightContainsExpressionContext,
  SingleArgumentFunctionContext,
  SingleExpressionContext,
  SingleFactorContext,
  SingleLogicExprContext,
  SingleTermContext,
  StringItemContext,
  StringLiteralContext,
  StringValueContext,
  StringValueListContext,
  SubstituteFunctionContext,
  SubtractionExprContext,
  SumExprContext,
  SumFunctionContext,
  SumIfFunctionContext,
  SumProductFunctionContext,
  ThreeArgumentsFunctionContext,
  ThreeExpressionArgsContext,
  ToDateFunctionContext,
  TodayLiteralContext,
  ToNumberFunctionContext,
  ToTextFunctionContext,
  TrimFunctionContext,
  TwoArgumentsFunctionContext,
  TwoExpressionArgsContext,
  TwoExpressionsContext,
  UnaryExprContext,
  UnaryExpressionContext,
  UpperFunctionContext,
  YearFunctionContext,
} from '../generated/formulaParser.js'
import { typeCheckFormulaTree } from './formula-type-checker.js'
import { FormulaErrorListener } from './FormulaErrorListener.js'

/**
 * Minimal column interface for display purposes in formula builder.
 * This is what getTableColumnDisplay and ColumnChipWidget actually need.
 */
export interface FormulaDisplayColumn {
  id: string
  name: string
  table: {
    id: string
    name: string
    parentTable: {
      name: string
    } | null
  }
}

/**
 * Language scope for formula parsing
 * - 'formula': Full formula language with arithmetic, functions, and logical expressions
 * - 'logicalExpression': Only logical expressions (comparisons, AND/OR/NOT, Contains, IN)
 */
export type LanguageScope = 'formula' | 'logicalExpression'

/**
 * Minimal column interface required for formula parsing and validation.
 * This interface only includes the fields actually used by parseText.
 */
export interface ParseableColumn {
  id: string
  type: DataType
  lookupColumn?: {
    type: DataType
  } | null
  fieldType?: 'formInput' | 'tableColumn'
}

export type ErrorToken = {
  start: number
  end: number
  message: string
  recognizer: Recognizer<Token>
  offendingSymbol: Token
  e: RecognitionException | undefined
  line: number
  charPositionInLine: number
  isBuilderError: boolean
}

export type LogicExpressionValueInput = {
  order: number
  value?: Record<string, unknown> | null
  columnId?: string | null
  nestedExpression?: LogicExpressionInput | null
  formula?: FormulaInput | null
  formFieldId?: string | null
  formInputId?: string | null
}

export type LogicExpressionInput = {
  type: LogicExpressionType
  condition?: LogicExpressionCondition | null
  values: LogicExpressionValueInput[]
  logicExpressionString?: string | null
}

/**
 * A root logic expression — one directly created by the UI.
 */
export type ParentLogicExpressionInput = Omit<LogicExpressionInput, 'logicExpressionString'> & {
  logicExpressionString: string
}

export type FormulaConditionInput = {
  condition: LogicExpressionInput
  order: number
  formula: FormulaInput
}

/**
 * The current-date (`TODAY`) / current-date-time (`NOW`) formula literals. Stored on a value node's
 * `dateTimeToken` so it can be told apart from a plain string literal of "TODAY"/"NOW" and resolved
 * to the actual date/time fresh at evaluation time.
 */
export type FormulaDateTimeToken = 'TODAY' | 'NOW'

export type FormulaValueInput = {
  tableColumnId?: string | null
  formInputId?: string | null
  formula?: FormulaInput | null
  conditions?: FormulaConditionInput[] | null
  /**
   * @deprecated Use the typed fields instead. Numeric literals dual-write `value` and
   * `valueNumber`; kept so older clients that only send `value` keep working. Strings belong in
   * `valueString`.
   */
  value?: number | null
  valueNumber?: number | null
  valueString?: string | null
  /**
   * Set when this node is a `TODAY`/`NOW` literal. Its `valueString` mirrors the token for legacy
   * storage, but this marker is what the type-checker, output inference, and resolver key off of.
   */
  dateTimeToken?: FormulaDateTimeToken | null
  order: number
}

export type FormulaInput = {
  operator: FormulaOperator
  formulaValues: FormulaValueInput[]
  columnOperationFilter?: LogicExpressionInput | null
  parentFormulaId?: string
  formulaString?: string
  outputType?: FormulaValueType
}

/**
 * A root formula — one directly created by the UI.
 */
export type ParentFormulaInput = Omit<FormulaInput, 'formulaString'> & {
  formulaString: string
}

/**
 * Optional parse-time features.
 *
 * `allowDateTimeTokens` opts a caller into the `TODAY`/`NOW` literals (default off; only
 * used in form-ulas).
 *
 * `allowMultiColumnAggregations` opts a caller into aggregations over more than one source column
 * (SUMPRODUCT). Default off: they pair their operands by source row, which only table formulas
 * can guarantee — a form's parallel inputs compact out blanks and lose that alignment. Every
 * parse path that reads a TABLE column's formula has to pass it, or it rejects valid formulas;
 * defaulting off is deliberate, since the opposite mistake (a form path silently accepting one)
 * produces wrong numbers instead of a loud error.
 */
export type ParseFormulaOptions = {
  allowDateTimeTokens?: boolean
  allowMultiColumnAggregations?: boolean
}

export type FormulaParseResults = {
  tokens: CommonToken[]
  errors: ErrorToken[]
  tree: ExprContext
  formula: FormulaInput | null
}

export type LogicExpressionParseResults = {
  tokens: CommonToken[]
  errors: ErrorToken[]
  tree: LogicExprContext
  formula: LogicExpressionInput | null
}

export type ParseResults = FormulaParseResults | LogicExpressionParseResults

/** Operators with a filtered form, the only ones that can carry a column operation filter */
const IF_OPERATORS: readonly FormulaOperator[] = Object.values(filteredKeywordToBaseOperator)

// Extending the FormulaInput type to include invokingStates
export type ExtendedFormulaInput = Omit<FormulaInput, 'columnOperationFilter' | 'formulaValues'> & {
  columnOperationFilter?: ExtendedLogicExpressionInput
  formulaValues: ExtendedFormulaValueInput[]
  invokingStates: string[]
}
export type ExtendedFormulaValueInput = Omit<FormulaValueInput, 'conditions' | 'formula'> & {
  conditions: ExtendedFormulaConditionInput[]
  formula?: ExtendedFormulaInput
  invokingStates: string[]
}

export type ExtendedLogicExpressionInput = Omit<LogicExpressionInput, 'values'> & {
  values: ExtendedLogicExpressionValueInput[]
  invokingStates: string[]
}

export type ExtendedLogicExpressionValueInput = Omit<
  LogicExpressionValueInput,
  'formula' | 'nestedExpression'
> & {
  formula?: ExtendedFormulaInput
  nestedExpression?: ExtendedLogicExpressionInput
  invokingStates: string[]
}

export type ExtendedFormulaConditionInput = Omit<FormulaConditionInput, 'condition' | 'formula'> & {
  condition: ExtendedLogicExpressionInput
  formula: ExtendedFormulaInput
}

/**
 * Given an ANTLR context, computes a unique invoking state string based on the invoking state and
 * the start and stop character indexes
 */
function getStateString(ctx: ParserRuleContext): string {
  return `${ctx.invokingState}-${ctx.start.start}-${ctx.stop?.stop}`
}

/**
 * Given a formula input and the parent invoking state key, recurisvely searches for the formula
 * that was set with that state.
 */
function getFormulaFromInvokingState(
  formula: ExtendedFormulaInput,
  parentState: string,
): ExtendedFormulaInput | undefined {
  if (formula.invokingStates.includes(parentState)) {
    return formula
  }

  // Recursively search for the formula
  const formulas = _.compact(
    formula.formulaValues.flatMap((formulaValue) => {
      if (formulaValue.formula) {
        return getFormulaFromInvokingState(formulaValue.formula, parentState)
      }
      const conditionFormulas = formulaValue.conditions.map((condition) =>
        getFormulaFromInvokingState(condition.formula, parentState),
      )
      return conditionFormulas
    }),
  )

  // Also search for formulas inside logic expression values
  if (formula.columnOperationFilter) {
    const logicExpressionFormulas = _.compact(
      getAllChildLogicExpressionValues(formula.columnOperationFilter).map((value) => {
        if (value.formula) {
          return getFormulaFromInvokingState(value.formula, parentState)
        }
        return undefined
      }),
    )
    formulas.push(...logicExpressionFormulas)
  }

  if (formulas.length > 1) {
    // This is an error state: multiple formulas found with the same invoking state
    return
  } else {
    return formulas[0]
  }
}

/**
 * Given a formula input and the parent invoking state key, recurisvely searches for the formula
 * value that was set with that state.
 */
function getParentFormulaValueByInvokingState(
  formula: ExtendedFormulaInput,
  parentState: string,
): ExtendedFormulaValueInput | undefined {
  // Checks all the formula's formula values
  const formulaValues = formula.formulaValues
  const matchedValues = _.compact(
    formulaValues.map((formulaValue) => {
      if (formulaValue.invokingStates.includes(parentState)) {
        return formulaValue
      }
      return undefined
    }),
  )
  if (matchedValues.length > 1) {
    // This is an error state: multiple expressions found with the same invoking state
    return
  } else if (matchedValues.length === 1) {
    return matchedValues[0]
  }

  // If none of the formula's formula values worked, check all the nested formulas
  const values = _.compact(
    formula.formulaValues.flatMap((formulaValue) => {
      if (formulaValue.formula) {
        return getParentFormulaValueByInvokingState(formulaValue.formula, parentState)
      }
      const conditionFormulas = formulaValue.conditions.map((condition) =>
        getParentFormulaValueByInvokingState(condition.formula, parentState),
      )
      return conditionFormulas
    }),
  )

  if (values.length > 1) {
    // This is an error state: multiple formula values found with the same invoking state
    return
  } else {
    return values[0]
  }
}

/**
 * Recursively get child logic expressions from the nested expression
 */
function getAllChildLogicExpressions(
  logicExpression: ExtendedLogicExpressionInput,
): ExtendedLogicExpressionInput[] {
  const nestedExpressions = logicExpression.values.map((value) => {
    if (value.nestedExpression) {
      return getAllChildLogicExpressions(value.nestedExpression)
    }

    return []
  })
  return [logicExpression, ...nestedExpressions.flat()]
}

/**
 * Recursively get all child logic expression values from a nested expression
 */
function getAllChildLogicExpressionValues(
  logicExpression: ExtendedLogicExpressionInput,
): ExtendedLogicExpressionValueInput[] {
  return logicExpression.values.flatMap((value) => {
    // If the value has a nested expression, recursively get its child logic expression values
    const nestedValues = value.nestedExpression
      ? getAllChildLogicExpressionValues(value.nestedExpression)
      : []
    // Include the current value and any nested values
    return [value, ...nestedValues]
  })
}

/**
 * Given a formula input and the parent invoking state key, recurisvely searches for the logic
 * expression that was set with that state.
 */
function getParentLogicExpressionByInvokingState(
  formula: ExtendedFormulaInput,
  parentState: string,
): ExtendedLogicExpressionInput | undefined {
  // Return if there is a column operation filter that is set with the parent state
  const expressions: ExtendedLogicExpressionInput[] = []

  if (formula.columnOperationFilter) {
    expressions.push(...getAllChildLogicExpressions(formula.columnOperationFilter))
  }

  // Return if one of the conditions has the parent state
  const conditions = formula.formulaValues
    .map((formulaValue) => formulaValue.conditions)
    .flat()
    .map((condition) => condition.condition)
  conditions.forEach((condition) => {
    expressions.push(...getAllChildLogicExpressions(condition))
  })

  const matchedExpressions = expressions.filter((expression) =>
    expression.invokingStates.includes(parentState),
  )
  if (matchedExpressions.length > 1) {
    // This is an error state: multiple expressions found with the same invoking state
    return
  } else if (matchedExpressions.length === 1) {
    return matchedExpressions[0]
  }

  // Recursively search for the logic expression
  const logicExpression = _.compact(
    formula.formulaValues.map((formulaValue) => {
      if (formulaValue.formula) {
        return getParentLogicExpressionByInvokingState(formulaValue.formula, parentState)
      }
      return undefined
    }),
  )

  if (logicExpression.length > 1) {
    // This is an error state: multiple expressions found with the same invoking state
    return
  } else {
    return logicExpression[0]
  }
}

/**
 * Given a formula input and the parent invoking state key, recursively searches for the logic
 * expression value that was set with that state.
 */
function getParentLogicExpressionValueByInvokingState(
  formula: ExtendedFormulaInput,
  parentState: string,
): ExtendedLogicExpressionValueInput | undefined {
  // Gather all logic expression values from the columnOperationFilter and conditions
  const expressionValues: ExtendedLogicExpressionValueInput[] = []

  if (formula.columnOperationFilter) {
    expressionValues.push(...getAllChildLogicExpressionValues(formula.columnOperationFilter))
  }

  formula.formulaValues.forEach((formulaValue) => {
    formulaValue.conditions.forEach((condition) => {
      expressionValues.push(...getAllChildLogicExpressionValues(condition.condition))
    })
  })

  // Check if any of the gathered expression values match the invoking state
  const matchedExpressionValues = expressionValues.filter((value) =>
    value.invokingStates.includes(parentState),
  )
  if (matchedExpressionValues.length > 1) {
    // This is an error state: multiple expression values found with the same invoking state
    return
  } else if (matchedExpressionValues.length === 1) {
    return matchedExpressionValues[0]
  }

  // Recursively search for the logic expression
  const logicExpressionValue = _.compact(
    formula.formulaValues.map((formulaValue) => {
      if (formulaValue.formula) {
        return getParentLogicExpressionValueByInvokingState(formulaValue.formula, parentState)
      }
      return undefined
    }),
  )

  if (logicExpressionValue.length > 1) {
    // This is an error state: multiple expression values found with the same invoking state
    return
  } else {
    return logicExpressionValue[0]
  }
}

/**
 * Given a formula, sets the context's invoking state in the list of invoking state that the parent
 * ctx's invoking state is in. This is used for pass through nodes in the generated AST. This will
 * update parent invoking states for formulas, logic expressions, and formula values
 *
 * To further clarify, in the tree that is generated, some nodes don't represent specific values,
 * but rather are used to organize formula concepts. Like a `singleArgumentFunction` represents a
 * pattern of `functionType ( singleArg )` so it would serve as a pass through node to relevant
 * nodes like `functionType` and `singleArg`. In those cases, we just want to add the invoking state
 * so we have the parent reference for its child nodes and then move on.
 */
function setFormulaInvokingState(
  ctx: ParserRuleContext,
  formula: ExtendedFormulaInput,
  getCurrentFormulaRoot?: () => ExtendedFormulaInput,
): void {
  // Use the current formula root if provided (for nested formulas), otherwise use the passed formula
  const rootFormula = getCurrentFormulaRoot ? getCurrentFormulaRoot() : formula

  const invokingState = getStateString(ctx)
  if (!ctx.parentCtx) {
    // If there is no parent, just set the top level formula states
    rootFormula.invokingStates.push(invokingState)
  } else {
    const parentState = getStateString(ctx.parentCtx)
    // If there is a parent formula, set the invoking state there
    const parentFormula = getFormulaFromInvokingState(rootFormula, parentState)
    if (parentFormula) {
      parentFormula.invokingStates.push(invokingState)
    }

    // If there is a parent logic expression, set the invoking state there
    const parentExpression = getParentLogicExpressionByInvokingState(rootFormula, parentState)
    if (parentExpression) {
      parentExpression.invokingStates.push(invokingState)
    }

    // If there is a parent formula value, set the invoking state there
    const parentFormulaValue = getParentFormulaValueByInvokingState(rootFormula, parentState)
    if (parentFormulaValue) {
      parentFormulaValue.invokingStates.push(invokingState)
    }

    // If there is a parent logic expression value, set the invoking state there
    const parentExpressionValue = getParentLogicExpressionValueByInvokingState(
      rootFormula,
      parentState,
    )
    if (parentExpressionValue) {
      parentExpressionValue.invokingStates.push(invokingState)
      // Also add invoking state to the formula inside the logic expression value (if it exists)
      // This allows child nodes to find and populate the formula
      if (parentExpressionValue.formula) {
        parentExpressionValue.formula.invokingStates.push(invokingState)
      }
    }
  }
}

/**
 * Either adds a new formula value with a new child formula using the provided operator or updates
 * the top-level formula operator depending on whether initializedFormula is set. If the provided
 * ctx doesn't have 3 parameters (and therefore isn't a formula), just treat it as a pass through
 * node instead.
 */
function createFormula(
  ctx: ParserRuleContext,
  formula: ExtendedFormulaInput,
  operator: FormulaOperator,
  initializedFormula: boolean,
): void {
  if (ctx.getChildCount() !== 3 && ctx.parentCtx) {
    // Pass through node
    setFormulaInvokingState(ctx, formula)
  } else if (ctx.parentCtx && initializedFormula) {
    // Sets the parent formula's formula values
    const parentFormula = getFormulaFromInvokingState(formula, getStateString(ctx.parentCtx))

    if (parentFormula && parentFormula.operator === operator) {
      // If the same operator is being added, just reuse the parent formula
      parentFormula.invokingStates.push(getStateString(ctx))
    } else if (parentFormula) {
      // If the operator is different, add a new formula value to the parent formula
      parentFormula.formulaValues.push({
        order: parentFormula.formulaValues.length + 1,
        formula: {
          operator,
          formulaValues: [],
          invokingStates: [getStateString(ctx)],
        },
        conditions: [],
        invokingStates: [],
      })
    }
  } else {
    // If the formula isn't initialized yet, sets the root formula operator
    formula.invokingStates.push(getStateString(ctx))
    formula.operator = operator
  }
}

/**
 * Either adds a formula value to the parent formula found based on the ctx, or updates the top
 * level formula if the formula value represents a formula, or adds a new formula value to the
 * top level formula if initializedFormula is `false`.
 */
function addFormulaValue(
  ctx: ParserRuleContext,
  formula: ExtendedFormulaInput,
  formulaValue: Omit<ExtendedFormulaValueInput, 'order'>,
  initializedFormula: boolean,
): void {
  if (ctx.parentCtx && initializedFormula) {
    const parentFormula = getFormulaFromInvokingState(formula, getStateString(ctx.parentCtx))

    if (parentFormula) {
      parentFormula.formulaValues.push({
        ...formulaValue,
        order: parentFormula.formulaValues.length + 1,
      })
    }
  } else if (formulaValue.formula) {
    // No need to create a new sub-function if the function is not initialized yet. Instead, just
    // update the current formula
    formula.invokingStates.push(...formulaValue.formula.invokingStates)
    formula.operator = formulaValue.formula.operator
  } else {
    formula.formulaValues.push({
      ...formulaValue,
      order: formula.formulaValues.length + 1,
    })
  }
}

/**
 * Sets the operator on the parent formula based on the ctx.
 */
function setFormulaOperator(
  ctx: ParserRuleContext,
  formula: ExtendedFormulaInput,
  operator: FormulaOperator,
): void {
  if (ctx.parentCtx) {
    const parentFormula = getFormulaFromInvokingState(formula, getStateString(ctx.parentCtx))
    if (parentFormula) {
      parentFormula.operator = operator
    }
  }
}

/**
 * Either creates a logic expression in the parent formula columnOperationFilter or does it in the
 * root formula depending on whether initializedFormula is true or false.
 */
function createLogicExpression(
  ctx: ParserRuleContext,
  formula: ExtendedFormulaInput,
  initializedFormula: boolean,
  type: LogicExpressionType,
  getCurrentFormulaRoot?: () => ExtendedFormulaInput,
): void {
  // Use the current formula root if provided (for nested formulas), otherwise use the passed formula
  const rootFormula = getCurrentFormulaRoot ? getCurrentFormulaRoot() : formula

  if (ctx.parentCtx && initializedFormula) {
    const parentFormula = getFormulaFromInvokingState(rootFormula, getStateString(ctx.parentCtx))
    // Set the parent formula's column operation filter for if operations
    if (parentFormula && IF_OPERATORS.includes(parentFormula.operator)) {
      parentFormula.columnOperationFilter = {
        type,
        values: [],
        invokingStates: [getStateString(ctx)],
      }
    }

    // If there is no parent formula, finds a parent logic expression instead and sets the
    // pass through value
    const expression = getParentLogicExpressionByInvokingState(
      rootFormula,
      getStateString(ctx.parentCtx),
    )
    if (expression && expression.type === type) {
      // If the same type is being added, just reuse the parent expression
      expression.invokingStates.push(getStateString(ctx))
    } else if (expression) {
      // If the type is different, add a new nested expression value to the parent expression
      expression.values.push({
        order: expression.values.length + 1,
        nestedExpression: {
          type,
          values: [],
          invokingStates: [getStateString(ctx)],
        },
        invokingStates: [getStateString(ctx)],
      })
    }

    // If there is a parent formula value instead of parent logic expression, add a condition to the
    // parent formula value
    const parentFormulaValue = getParentFormulaValueByInvokingState(
      rootFormula,
      getStateString(ctx.parentCtx),
    )
    if (parentFormulaValue) {
      // The condition's formula's parent should be the last common parent between a logic
      // expression and a formula. This is the `TwoExpressionContext` where the first expression is
      // a logic expression and the second is a formula.
      let formulaNode: ParserRuleContext = ctx
      while (formulaNode.parentCtx && !(formulaNode instanceof TwoExpressionsContext)) {
        formulaNode = formulaNode.parentCtx
      }

      parentFormulaValue.conditions.push({
        order: parentFormulaValue.conditions.length + 1,
        condition: {
          type,
          values: [],
          invokingStates: [getStateString(ctx)],
        },
        formula: {
          // Create an empty formula that will be updated later in the traversal
          operator: FormulaOperator.ADDITION,
          formulaValues: [],
          invokingStates: [getStateString(formulaNode)],
        },
      })
    }
  } else {
    // If the formula isn't initialized, sets the top level formula's columnOperationFilter
    // This handles both IF operators and standalone logic expressions (when parsing with logicalExpression scope)
    rootFormula.columnOperationFilter = {
      type,
      values: [],
      invokingStates: [getStateString(ctx)],
    }
  }
}

/**
 * Adds the provided logic expression value to the parent logic expression
 */
function addLogicExpressionValue(
  ctx: ParserRuleContext,
  formula: ExtendedFormulaInput,
  value: Omit<ExtendedLogicExpressionValueInput, 'order' | 'invokingStates'>,
  columns: ParseableColumn[],
  errors: ErrorToken[],
  leftValueCtx: ParserRuleContext | null,
  getCurrentFormulaRoot?: () => ExtendedFormulaInput,
): void {
  // Use the current formula root if provided (for nested formulas), otherwise use the passed formula
  const rootFormula = getCurrentFormulaRoot ? getCurrentFormulaRoot() : formula

  if (ctx.parentCtx) {
    const expression = getParentLogicExpressionByInvokingState(
      rootFormula,
      getStateString(ctx.parentCtx),
    )
    if (expression) {
      const order = expression.values.length + 1
      expression.values.push({
        order,
        ...value,
        invokingStates: [getStateString(ctx)],
      })

      // Check valid logic expression after adding the second logic value
      if (order === 2) {
        checkValidLogicExpression(ctx, expression, columns, errors, order, leftValueCtx)
      }
    }
  }
}

/**
 * Type guard for array type logic expression values
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isArrayValue<T>(value: any): value is { type: DossDataFormat.ARRAY; data: T[] } {
  return value && value.type === DossDataFormat.ARRAY && Array.isArray(value.data)
}

/**
 * Adds the provided value to the parent logic expression value's value data array
 */
function addValueToLogicExpressionValue<T>(
  parentCtx: ParserRuleContext,
  formula: ExtendedFormulaInput,
  value: T,
): void {
  const expressionValue = getParentLogicExpressionValueByInvokingState(
    formula,
    getStateString(parentCtx),
  )

  if (expressionValue && isArrayValue(expressionValue.value)) {
    expressionValue.value.data.push(value)
  } else {
    throw new Error('Parent logic expression value is not of type ARRAY')
  }
}

/**
 * Sets the provided logic expression condition type on the parent logic expression
 */
function setLogicExpressionCondition(
  ctx: ParserRuleContext,
  formula: ExtendedFormulaInput,
  condition: LogicExpressionCondition,
  columns: ParseableColumn[],
  errors: ErrorToken[],
  leftValueCtx: ParserRuleContext | null,
  getCurrentFormulaRoot?: () => ExtendedFormulaInput,
): void {
  // Use the current formula root if provided (for nested formulas), otherwise use the passed formula
  const rootFormula = getCurrentFormulaRoot ? getCurrentFormulaRoot() : formula

  if (ctx.parentCtx) {
    const expression = getParentLogicExpressionByInvokingState(
      rootFormula,
      getStateString(ctx.parentCtx),
    )
    if (expression) {
      expression.type = expression.type || LogicExpressionType.COMPARISON
      expression.condition = condition
      checkValidLogicExpression(ctx, expression, columns, errors, 0, leftValueCtx)
    }
  }
}

/**
 * Traverses the ANTLR AST to generate a FormulaInput
 */
export class FormulaModelBuilder extends formulaListener {
  private initializedFormula = false
  // Stacks to scope nested formulas
  private formulaStack: ExtendedFormulaInput[] = []
  private initializedFormulaStack: boolean[] = []

  private getCurrentFormulaRoot(): ExtendedFormulaInput {
    return this.formulaStack[this.formulaStack.length - 1] ?? this.formula
  }

  private formula: ExtendedFormulaInput = {
    operator: FormulaOperator.ADDITION,
    formulaValues: [],
    invokingStates: [],
  }
  private columns: ParseableColumn[]
  private errors: ErrorToken[] = []
  private currentLeftLogicExpressionValue: ParserRuleContext | null = null
  private readonly options: ParseFormulaOptions

  constructor(columns: ParseableColumn[], options: ParseFormulaOptions = {}) {
    super()
    this.columns = columns
    this.options = options
  }

  getErrors(): ErrorToken[] {
    return this.errors
  }

  // No terminal, these are all pass through nodes where we just set the invoking state chain
  enterExpr = (ctx: ExprContext): void => {
    setFormulaInvokingState(ctx, this.getCurrentFormulaRoot(), () => this.getCurrentFormulaRoot())
  }
  enterSumExpression = (ctx: SumExprContext): void => {
    setFormulaInvokingState(ctx, this.getCurrentFormulaRoot(), () => this.getCurrentFormulaRoot())
  }
  enterFormulaInLogic = (ctx: FormulaInLogicContext): void => {
    if (!ctx.parentCtx) {
      throw new Error('Formula cannot be root context of logic expression')
    }

    // 1) attach to global tree - use global formula here since we're adding to the parent's logic expression
    setFormulaInvokingState(ctx, this.formula, () => this.getCurrentFormulaRoot())

    addLogicExpressionValue(
      ctx,
      this.formula,
      {
        formula: {
          operator: FormulaOperator.ADDITION,
          formulaValues: [],
          invokingStates: [getStateString(ctx)],
        },
      },
      this.columns,
      this.errors,
      this.currentLeftLogicExpressionValue,
      () => this.getCurrentFormulaRoot(),
    )

    // 2) find the parent logic expression using the PARENT ctx state
    const parentExpression = getParentLogicExpressionByInvokingState(
      this.getCurrentFormulaRoot(),
      getStateString(ctx.parentCtx),
    )
    if (!parentExpression) {
      return
    }

    const stateString = getStateString(ctx)

    // 3) locate the placeholder we just inserted and push it
    const inserted = parentExpression.values.find((v) =>
      v.formula?.invokingStates?.includes(stateString),
    )
    if (!inserted?.formula) {
      return
    }

    this.formulaStack.push(inserted.formula)

    // 4) reset initializedFormula for nested parse
    this.initializedFormulaStack.push(this.initializedFormula)
    this.initializedFormula = false
  }
  enterSingleTerm = (ctx: SingleTermContext): void => {
    setFormulaInvokingState(ctx, this.getCurrentFormulaRoot(), () => this.getCurrentFormulaRoot())
  }
  enterSingleFactor = (ctx: SingleFactorContext): void => {
    setFormulaInvokingState(ctx, this.getCurrentFormulaRoot(), () => this.getCurrentFormulaRoot())
  }
  enterNestedSumExpr = (ctx: NestedSumExprContext): void => {
    setFormulaInvokingState(ctx, this.getCurrentFormulaRoot(), () => this.getCurrentFormulaRoot())
  }
  enterFunction = (ctx: FunctionContext): void => {
    setFormulaInvokingState(ctx, this.getCurrentFormulaRoot(), () => this.getCurrentFormulaRoot())
  }
  enterSingleExpression = (ctx: SingleExpressionContext): void => {
    setFormulaInvokingState(ctx, this.getCurrentFormulaRoot(), () => this.getCurrentFormulaRoot())
  }
  enterMultipleExpressions = (ctx: MultipleExpressionsContext): void => {
    setFormulaInvokingState(ctx, this.getCurrentFormulaRoot(), () => this.getCurrentFormulaRoot())
  }
  enterTwoExpressionArgs = (ctx: TwoExpressionArgsContext): void => {
    setFormulaInvokingState(ctx, this.getCurrentFormulaRoot(), () => this.getCurrentFormulaRoot())
  }
  enterThreeExpressionArgs = (ctx: ThreeExpressionArgsContext): void => {
    setFormulaInvokingState(ctx, this.getCurrentFormulaRoot(), () => this.getCurrentFormulaRoot())
  }
  enterIfsFunction = (ctx: IfsFunctionContext): void => {
    setFormulaInvokingState(ctx, this.getCurrentFormulaRoot(), () => this.getCurrentFormulaRoot())
  }
  enterDirectValue = (ctx: DirectValueContext): void => {
    setFormulaInvokingState(ctx, this.getCurrentFormulaRoot(), () => this.getCurrentFormulaRoot())
  }
  enterBinaryLogicExpr = (ctx: BinaryLogicExprContext): void => {
    setFormulaInvokingState(ctx, this.formula, () => this.getCurrentFormulaRoot())
    const firstChild = ctx.getChild(0)
    if (firstChild instanceof ParserRuleContext) {
      this.currentLeftLogicExpressionValue = firstChild
    }
  }
  enterScalarRightContainsExpr = (ctx: ScalarRightContainsExprContext): void => {
    setFormulaInvokingState(ctx, this.formula, () => this.getCurrentFormulaRoot())
    const firstChild = ctx.getChild(0)
    if (firstChild instanceof ParserRuleContext) {
      this.currentLeftLogicExpressionValue = firstChild
    }
  }
  enterArrayRightContainsExpr = (ctx: ArrayRightContainsExprContext): void => {
    setFormulaInvokingState(ctx, this.formula, () => this.getCurrentFormulaRoot())
    const firstChild = ctx.getChild(0)
    if (firstChild instanceof ParserRuleContext) {
      this.currentLeftLogicExpressionValue = firstChild
    }
  }
  enterUnaryExpr = (ctx: UnaryExprContext): void => {
    setFormulaInvokingState(ctx, this.formula, () => this.getCurrentFormulaRoot())
  }
  enterInExpr = (ctx: InExprContext): void => {
    setFormulaInvokingState(ctx, this.formula, () => this.getCurrentFormulaRoot())
    const firstChild = ctx.getChild(0)
    if (firstChild instanceof ParserRuleContext) {
      this.currentLeftLogicExpressionValue = firstChild
    }
  }
  enterNotExpression = (ctx: NotExpressionContext): void => {
    setFormulaInvokingState(ctx, this.formula, () => this.getCurrentFormulaRoot())
  }
  enterSingleLogicExpr = (ctx: SingleLogicExprContext): void => {
    setFormulaInvokingState(ctx, this.formula, () => this.getCurrentFormulaRoot())
  }
  enterParenthesizedLogicExpr = (ctx: ParenthesizedLogicExprContext): void => {
    setFormulaInvokingState(ctx, this.formula, () => this.getCurrentFormulaRoot())
  }
  enterParenthesizedLogicTerm = (ctx: ParenthesizedLogicTermContext): void => {
    setFormulaInvokingState(ctx, this.formula, () => this.getCurrentFormulaRoot())
  }
  enterTwoExpressions = (ctx: TwoExpressionsContext): void => {
    setFormulaInvokingState(ctx, this.formula, () => this.getCurrentFormulaRoot())
  }

  /**
   * Skip over nodes that are for invalid input. These are handled by the Formula Error Listener
   */
  enterInvalidInput = (): null => null

  // Has terminal (ADDITION, SUBTRACTION, MULTIPLICATION, DIVISION)
  enterAdditionExpr = (ctx: AdditionExprContext): void => {
    createFormula(
      ctx,
      this.getCurrentFormulaRoot(),
      FormulaOperator.ADDITION,
      this.initializedFormula,
    )
    this.initializedFormula = true
  }
  enterSubtractionExpr = (ctx: SubtractionExprContext): void => {
    createFormula(
      ctx,
      this.getCurrentFormulaRoot(),
      FormulaOperator.SUBTRACTION,
      this.initializedFormula,
    )
    this.initializedFormula = true
  }
  enterMultiplicationExpr = (ctx: MultiplicationExprContext): void => {
    createFormula(
      ctx,
      this.getCurrentFormulaRoot(),
      FormulaOperator.MULTIPLICATION,
      this.initializedFormula,
    )
    this.initializedFormula = true
  }
  enterDivisionExpr = (ctx: DivisionExprContext): void => {
    createFormula(
      ctx,
      this.getCurrentFormulaRoot(),
      FormulaOperator.DIVISION,
      this.initializedFormula,
    )
    this.initializedFormula = true
  }

  // Has terminal formula value (columnId, formInputId, or number)
  enterColumnRef = (ctx: ColumnRefContext): void => {
    if (!ctx.parentCtx) {
      throw new Error('Column ID cannot be root context of formula')
    }

    const fieldId = getTableColumnId(ctx)
    const field = this.columns.find((col) => col.id === fieldId)

    // Normal formula path
    const asFormula =
      field?.fieldType === 'formInput' ? { formInputId: fieldId } : { tableColumnId: fieldId }

    addFormulaValue(
      ctx,
      this.getCurrentFormulaRoot(),
      { ...asFormula, conditions: [], invokingStates: [getStateString(ctx)] },
      this.initializedFormula,
    )
  }
  enterNumber = (ctx: NumberContext): void => {
    if (!ctx.parentCtx) {
      throw new Error('Number cannot be root context of formula')
    }

    const number = parseFloat(ctx.NUMBER().getText())

    addFormulaValue(
      ctx,
      this.getCurrentFormulaRoot(),
      {
        value: number,
        valueNumber: number,
        conditions: [],
        invokingStates: [getStateString(ctx)],
      },
      this.initializedFormula,
    )
  }
  enterNegativeNumber = (ctx: NegativeNumberContext): void => {
    if (!ctx.parentCtx) {
      throw new Error('Negative number cannot be root context of formula')
    }

    const number = parseFloat(ctx.NUMBER().getText())
    const negativeNumber = number * -1

    addFormulaValue(
      ctx,
      this.getCurrentFormulaRoot(),
      {
        value: negativeNumber,
        valueNumber: negativeNumber,
        conditions: [],
        invokingStates: [getStateString(ctx)],
      },
      this.initializedFormula,
    )
  }

  enterStringLiteral = (ctx: StringLiteralContext): void => {
    if (!ctx.parentCtx) {
      throw new Error('String literal cannot be root context of formula')
    }

    const stringValue = getStringValue(ctx)

    addFormulaValue(
      ctx,
      this.getCurrentFormulaRoot(),
      { valueString: stringValue, conditions: [], invokingStates: [getStateString(ctx)] },
      this.initializedFormula,
    )
  }

  enterTodayLiteral = (ctx: TodayLiteralContext): void => {
    this.addDateTimeToken(ctx, 'TODAY')
  }

  enterNowLiteral = (ctx: NowLiteralContext): void => {
    this.addDateTimeToken(ctx, 'NOW')
  }

  // Parse TODAY/NOW here (but reject w/error message if `allowDateTimeTokens` is false).
  private addDateTimeToken(ctx: ParserRuleContext, token: FormulaDateTimeToken): void {
    if (!this.options.allowDateTimeTokens) {
      this.errors.push(createErrorToken(ctx, `${token} is only available in form formulas`))
      return
    }
    addFormulaValue(
      ctx,
      this.getCurrentFormulaRoot(),
      {
        valueString: token,
        dateTimeToken: token,
        conditions: [],
        invokingStates: [getStateString(ctx)],
      },
      this.initializedFormula,
    )
  }

  // Create a formula here and set this invoking state as the parent. Then when the function type
  // (i.e. SUM) is found, set this formula's type.
  enterSingleArgumentFunction = (ctx: SingleArgumentFunctionContext): void => {
    addFormulaValue(
      ctx,
      this.getCurrentFormulaRoot(),
      {
        formula: {
          operator: FormulaOperator.SUM,
          formulaValues: [],
          invokingStates: [getStateString(ctx)],
        },
        conditions: [],
        invokingStates: [],
      },
      this.initializedFormula,
    )
    this.initializedFormula = true
  }
  enterMultipleArgumentsFunction = (ctx: MultipleArgumentsFunctionContext): void => {
    addFormulaValue(
      ctx,
      this.getCurrentFormulaRoot(),
      {
        formula: {
          operator: FormulaOperator.CONCAT,
          formulaValues: [],
          invokingStates: [getStateString(ctx)],
        },
        conditions: [],
        invokingStates: [],
      },
      this.initializedFormula,
    )
    this.initializedFormula = true
  }
  enterTwoArgumentsFunction = (ctx: TwoArgumentsFunctionContext): void => {
    addFormulaValue(
      ctx,
      this.getCurrentFormulaRoot(),
      {
        formula: {
          operator: FormulaOperator.LEFT,
          formulaValues: [],
          invokingStates: [getStateString(ctx)],
        },
        conditions: [],
        invokingStates: [],
      },
      this.initializedFormula,
    )
    this.initializedFormula = true
  }
  enterThreeArgumentsFunction = (ctx: ThreeArgumentsFunctionContext): void => {
    addFormulaValue(
      ctx,
      this.getCurrentFormulaRoot(),
      {
        formula: {
          operator: FormulaOperator.SUBSTITUTE,
          formulaValues: [],
          invokingStates: [getStateString(ctx)],
        },
        conditions: [],
        invokingStates: [],
      },
      this.initializedFormula,
    )
    this.initializedFormula = true
  }
  enterIfFunction = (ctx: IfFunctionContext): void => {
    addFormulaValue(
      ctx,
      this.getCurrentFormulaRoot(),
      {
        formula: {
          operator: FormulaOperator.ADDITION,
          formulaValues: [],
          invokingStates: [getStateString(ctx)],
        },
        conditions: [],
        invokingStates: [],
      },
      this.initializedFormula,
    )
    this.initializedFormula = true
  }
  enterEvenExpressions = (ctx: EvenExpressionsContext): void => {
    addFormulaValue(
      ctx,
      this.getCurrentFormulaRoot(),
      {
        conditions: [],
        invokingStates: [getStateString(ctx)],
      },
      this.initializedFormula,
    )
    this.initializedFormula = true
  }

  // Has terminal function operator (MAX, MIN, COUNT, SUM, etc.)
  enterMaxFunction = (ctx: MaxFunctionContext): void => {
    setFormulaOperator(ctx, this.getCurrentFormulaRoot(), FormulaOperator.MAX)
  }
  enterMinFunction = (ctx: MinFunctionContext): void => {
    setFormulaOperator(ctx, this.getCurrentFormulaRoot(), FormulaOperator.MIN)
  }
  enterSumFunction = (ctx: SumFunctionContext): void => {
    setFormulaOperator(ctx, this.getCurrentFormulaRoot(), FormulaOperator.SUM)
  }
  enterCountFunction = (ctx: CountFunctionContext): void => {
    setFormulaOperator(ctx, this.getCurrentFormulaRoot(), FormulaOperator.COUNT)
  }
  enterCountDistinctFunction = (ctx: CountDistinctFunctionContext): void => {
    setFormulaOperator(ctx, this.getCurrentFormulaRoot(), FormulaOperator.COUNT_DISTINCT)
  }
  enterAverageFunction = (ctx: AverageFunctionContext): void => {
    setFormulaOperator(ctx, this.getCurrentFormulaRoot(), FormulaOperator.AVERAGE)
  }
  enterSumProductFunction = (ctx: SumProductFunctionContext): void => {
    if (!this.options.allowMultiColumnAggregations) {
      this.errors.push(createErrorToken(ctx, 'SUMPRODUCT is only available in table formulas'))
      return
    }
    setFormulaOperator(ctx, this.getCurrentFormulaRoot(), FormulaOperator.SUMPRODUCT)
  }
  enterCeilFunction = (ctx: CeilFunctionContext): void => {
    setFormulaOperator(ctx, this.getCurrentFormulaRoot(), FormulaOperator.CEIL)
  }
  enterFloorFunction = (ctx: FloorFunctionContext): void => {
    setFormulaOperator(ctx, this.getCurrentFormulaRoot(), FormulaOperator.FLOOR)
  }
  enterRoundFunction = (ctx: RoundFunctionContext): void => {
    setFormulaOperator(ctx, this.getCurrentFormulaRoot(), FormulaOperator.ROUND)
  }
  enterSumIfFunction = (ctx: SumIfFunctionContext): void => {
    setFormulaOperator(ctx, this.getCurrentFormulaRoot(), FormulaOperator.SUM)
  }
  enterCountIfFunction = (ctx: CountIfFunctionContext): void => {
    setFormulaOperator(ctx, this.getCurrentFormulaRoot(), FormulaOperator.COUNT)
  }
  enterAverageIfFunction = (ctx: AverageIfFunctionContext): void => {
    setFormulaOperator(ctx, this.getCurrentFormulaRoot(), FormulaOperator.AVERAGE)
  }
  enterMinIfsFunction = (ctx: MinIfsFunctionContext): void => {
    setFormulaOperator(ctx, this.getCurrentFormulaRoot(), FormulaOperator.MIN)
  }
  enterMaxIfsFunction = (ctx: MaxIfsFunctionContext): void => {
    setFormulaOperator(ctx, this.getCurrentFormulaRoot(), FormulaOperator.MAX)
  }

  enterUpperFunction = (ctx: UpperFunctionContext): void => {
    setFormulaOperator(ctx, this.getCurrentFormulaRoot(), FormulaOperator.UPPER)
  }
  enterLowerFunction = (ctx: LowerFunctionContext): void => {
    setFormulaOperator(ctx, this.getCurrentFormulaRoot(), FormulaOperator.LOWER)
  }
  enterTrimFunction = (ctx: TrimFunctionContext): void => {
    setFormulaOperator(ctx, this.getCurrentFormulaRoot(), FormulaOperator.TRIM)
  }
  enterLenFunction = (ctx: LenFunctionContext): void => {
    setFormulaOperator(ctx, this.getCurrentFormulaRoot(), FormulaOperator.LEN)
  }
  enterConcatFunction = (ctx: ConcatFunctionContext): void => {
    setFormulaOperator(ctx, this.getCurrentFormulaRoot(), FormulaOperator.CONCAT)
  }
  enterLeftFunction = (ctx: LeftFunctionContext): void => {
    setFormulaOperator(ctx, this.getCurrentFormulaRoot(), FormulaOperator.LEFT)
  }
  enterRightFunction = (ctx: RightFunctionContext): void => {
    setFormulaOperator(ctx, this.getCurrentFormulaRoot(), FormulaOperator.RIGHT)
  }
  enterMidFunction = (ctx: MidFunctionContext): void => {
    setFormulaOperator(ctx, this.getCurrentFormulaRoot(), FormulaOperator.MID)
  }
  enterSubstituteFunction = (ctx: SubstituteFunctionContext): void => {
    setFormulaOperator(ctx, this.getCurrentFormulaRoot(), FormulaOperator.SUBSTITUTE)
  }

  enterDateFunction = (ctx: DateFunctionContext): void => {
    setFormulaOperator(ctx, this.getCurrentFormulaRoot(), FormulaOperator.DATE)
  }
  enterDateaddFunction = (ctx: DateaddFunctionContext): void => {
    setFormulaOperator(ctx, this.getCurrentFormulaRoot(), FormulaOperator.DATEADD)
  }
  enterDatediffFunction = (ctx: DatediffFunctionContext): void => {
    setFormulaOperator(ctx, this.getCurrentFormulaRoot(), FormulaOperator.DATEDIFF)
  }
  enterYearFunction = (ctx: YearFunctionContext): void => {
    setFormulaOperator(ctx, this.getCurrentFormulaRoot(), FormulaOperator.YEAR)
  }
  enterMonthFunction = (ctx: MonthFunctionContext): void => {
    setFormulaOperator(ctx, this.getCurrentFormulaRoot(), FormulaOperator.MONTH)
  }
  enterDayFunction = (ctx: DayFunctionContext): void => {
    setFormulaOperator(ctx, this.getCurrentFormulaRoot(), FormulaOperator.DAY)
  }

  enterToTextFunction = (ctx: ToTextFunctionContext): void => {
    setFormulaOperator(ctx, this.getCurrentFormulaRoot(), FormulaOperator.TO_TEXT)
  }
  enterToNumberFunction = (ctx: ToNumberFunctionContext): void => {
    setFormulaOperator(ctx, this.getCurrentFormulaRoot(), FormulaOperator.TO_NUMBER)
  }
  enterToDateFunction = (ctx: ToDateFunctionContext): void => {
    setFormulaOperator(ctx, this.getCurrentFormulaRoot(), FormulaOperator.TO_DATE)
  }

  // Create a logic expression here and set this invoking state as the parent.
  enterAndChainedLogicExpr = (ctx: AndChainedLogicExprContext): void => {
    createLogicExpression(ctx, this.formula, this.initializedFormula, LogicExpressionType.AND, () =>
      this.getCurrentFormulaRoot(),
    )
    this.initializedFormula = true
  }
  enterOrChainedLogicExpr = (ctx: OrChainedLogicExprContext): void => {
    createLogicExpression(ctx, this.formula, this.initializedFormula, LogicExpressionType.OR, () =>
      this.getCurrentFormulaRoot(),
    )
    this.initializedFormula = true
  }
  enterNotExpr = (ctx: NotExprContext): void => {
    createLogicExpression(ctx, this.formula, this.initializedFormula, LogicExpressionType.NOT, () =>
      this.getCurrentFormulaRoot(),
    )
    this.initializedFormula = true
  }
  enterComparisonExpression = (ctx: ComparisonExpressionContext): void => {
    createLogicExpression(
      ctx,
      this.formula,
      this.initializedFormula,
      LogicExpressionType.COMPARISON,
      () => this.getCurrentFormulaRoot(),
    )
    this.initializedFormula = true
  }
  enterScalarRightContainsExpression = (ctx: ScalarRightContainsExpressionContext): void => {
    createLogicExpression(
      ctx,
      this.formula,
      this.initializedFormula,
      LogicExpressionType.COMPARISON,
      () => this.getCurrentFormulaRoot(),
    )
    this.initializedFormula = true
  }
  enterArrayRightContainsExpression = (ctx: ArrayRightContainsExpressionContext): void => {
    createLogicExpression(
      ctx,
      this.formula,
      this.initializedFormula,
      LogicExpressionType.COMPARISON,
      () => this.getCurrentFormulaRoot(),
    )
    this.initializedFormula = true
  }
  enterUnaryExpression = (ctx: UnaryExpressionContext): void => {
    createLogicExpression(
      ctx,
      this.formula,
      this.initializedFormula,
      LogicExpressionType.UNARY,
      () => this.getCurrentFormulaRoot(),
    )
    this.initializedFormula = true
  }
  enterInExpression = (ctx: InExpressionContext): void => {
    createLogicExpression(
      ctx,
      this.formula,
      this.initializedFormula,
      LogicExpressionType.COMPARISON,
      () => this.getCurrentFormulaRoot(),
    )
    this.initializedFormula = true
  }
  enterBooleanExpression = (ctx: BooleanExpressionContext): void => {
    createLogicExpression(
      ctx,
      this.formula,
      this.initializedFormula,
      LogicExpressionType.BOOLEAN,
      () => this.getCurrentFormulaRoot(),
    )
    this.initializedFormula = true
    const expression = getParentLogicExpressionByInvokingState(
      this.getCurrentFormulaRoot(),
      getStateString(ctx),
    )
    const booleanText = ctx.BOOLEAN().getText().toLowerCase()
    const boolean = booleanText === 'true'
    if (expression) {
      expression.values.push({
        order: expression.values.length + 1,
        value: { type: DossDataFormat.BOOLEAN, data: boolean },
        invokingStates: [getStateString(ctx)],
      })
    }
  }

  // When exiting, clear the current left expression value
  exitBinaryLogicExpr = (): void => {
    this.currentLeftLogicExpressionValue = null
  }
  exitScalarRightContainsExpr = (): void => {
    this.currentLeftLogicExpressionValue = null
  }
  exitArrayRightContainsExpr = (): void => {
    this.currentLeftLogicExpressionValue = null
  }
  exitInExpr = (): void => {
    this.currentLeftLogicExpressionValue = null
  }
  exitFormulaInLogic = (ctx: FormulaInLogicContext): void => {
    if (!ctx.parentCtx) {
      return
    }

    // Pop the formula from stack first before doing lookups
    this.formulaStack.pop()
    const prevInit = this.initializedFormulaStack.pop()

    const stateString = getStateString(ctx)

    // Parent expression is keyed off the PARENT ctx, not the FormulaInLogic ctx
    // Use the formula root BEFORE we popped (which would be the parent in the stack)
    const searchRoot =
      this.formulaStack.length > 0 ? this.formulaStack[this.formulaStack.length - 1] : this.formula

    const parentExpression = getParentLogicExpressionByInvokingState(
      searchRoot,
      getStateString(ctx.parentCtx),
    )

    if (!parentExpression) {
      this.initializedFormula = prevInit ?? this.initializedFormula
      return
    }

    const formulaValueIndex = parentExpression.values.findIndex((v) =>
      v.formula?.invokingStates?.includes(stateString),
    )
    if (formulaValueIndex === -1) {
      this.initializedFormula = prevInit ?? this.initializedFormula
      return
    }

    const formulaValue = parentExpression.values[formulaValueIndex]
    const formula = formulaValue.formula
    if (!formula) {
      this.initializedFormula = prevInit ?? this.initializedFormula
      return
    }

    // Checks if the formula is incomplete, in which case it should be treated like a
    // logic expression value instead
    const isTrivialFormula =
      formula.operator === FormulaOperator.ADDITION &&
      formula.formulaValues.length === 1 &&
      !formula.formulaValues[0].formula &&
      (!formula.formulaValues[0].conditions || formula.formulaValues[0].conditions.length === 0)

    if (isTrivialFormula) {
      const singleValue = formula.formulaValues[0]
      const unwrappedValue: ExtendedLogicExpressionValueInput = {
        order: formulaValue.order,
        invokingStates: [],
      }

      // Handle literal values - convert from formula format to logic expression format. The
      // typed fields win; the deprecated `value` field is a fallback so trees from older clients
      // that only populate it keep working.
      if (singleValue.valueString !== undefined && singleValue.valueString !== null) {
        unwrappedValue.value = {
          type: DossDataFormat.STRING,
          data: singleValue.valueString,
        }
      } else if (singleValue.valueNumber !== undefined && singleValue.valueNumber !== null) {
        unwrappedValue.value = {
          type: DossDataFormat.NUMBER,
          data: singleValue.valueNumber,
        }
      } else if (typeof singleValue.value === 'number') {
        unwrappedValue.value = {
          type: DossDataFormat.NUMBER,
          data: singleValue.value,
        }
      }

      if (singleValue.formInputId) {
        unwrappedValue.formInputId = singleValue.formInputId
      }
      if (singleValue.tableColumnId) {
        unwrappedValue.columnId = singleValue.tableColumnId
      }
      if (singleValue.invokingStates) {
        unwrappedValue.invokingStates = singleValue.invokingStates
      }

      parentExpression.values[formulaValueIndex] = unwrappedValue
    }

    this.initializedFormula = prevInit ?? this.initializedFormula
  }
  // Has terminal (>, >=, <, etc.)
  enterGreaterThan = (ctx: GreaterThanContext): void => {
    setLogicExpressionCondition(
      ctx,
      this.formula,
      LogicExpressionCondition.GREATER_THAN,
      this.columns,
      this.errors,
      this.currentLeftLogicExpressionValue,
      () => this.getCurrentFormulaRoot(),
    )
  }
  enterGreaterThanOrEqual = (ctx: GreaterThanOrEqualContext): void => {
    setLogicExpressionCondition(
      ctx,
      this.formula,
      LogicExpressionCondition.GREATER_THAN_OR_EQUAL,
      this.columns,
      this.errors,
      this.currentLeftLogicExpressionValue,
      () => this.getCurrentFormulaRoot(),
    )
  }
  enterLessThan = (ctx: LessThanContext): void => {
    setLogicExpressionCondition(
      ctx,
      this.formula,
      LogicExpressionCondition.LESS_THAN,
      this.columns,
      this.errors,
      this.currentLeftLogicExpressionValue,
      () => this.getCurrentFormulaRoot(),
    )
  }
  enterLessThanOrEqual = (ctx: LessThanOrEqualContext): void => {
    setLogicExpressionCondition(
      ctx,
      this.formula,
      LogicExpressionCondition.LESS_THAN_OR_EQUAL,
      this.columns,
      this.errors,
      this.currentLeftLogicExpressionValue,
      () => this.getCurrentFormulaRoot(),
    )
  }
  enterEqual = (ctx: EqualContext): void => {
    setLogicExpressionCondition(
      ctx,
      this.formula,
      LogicExpressionCondition.EQUAL,
      this.columns,
      this.errors,
      this.currentLeftLogicExpressionValue,
      () => this.getCurrentFormulaRoot(),
    )
  }
  enterNotEqual = (ctx: NotEqualContext): void => {
    setLogicExpressionCondition(
      ctx,
      this.formula,
      LogicExpressionCondition.NOT_EQUAL,
      this.columns,
      this.errors,
      this.currentLeftLogicExpressionValue,
      () => this.getCurrentFormulaRoot(),
    )
  }
  enterContains = (ctx: ContainsContext): void => {
    setLogicExpressionCondition(
      ctx,
      this.formula,
      LogicExpressionCondition.CONTAINS,
      this.columns,
      this.errors,
      this.currentLeftLogicExpressionValue,
      () => this.getCurrentFormulaRoot(),
    )
  }
  enterDoesNotContain = (ctx: DoesNotContainContext): void => {
    setLogicExpressionCondition(
      ctx,
      this.formula,
      LogicExpressionCondition.DOES_NOT_CONTAIN,
      this.columns,
      this.errors,
      this.currentLeftLogicExpressionValue,
      () => this.getCurrentFormulaRoot(),
    )
  }
  enterIn = (ctx: InContext): void => {
    setLogicExpressionCondition(
      ctx,
      this.formula,
      LogicExpressionCondition.IN,
      this.columns,
      this.errors,
      this.currentLeftLogicExpressionValue,
      () => this.getCurrentFormulaRoot(),
    )
  }
  enterNotIn = (ctx: NotInContext): void => {
    setLogicExpressionCondition(
      ctx,
      this.formula,
      LogicExpressionCondition.NOT_IN,
      this.columns,
      this.errors,
      this.currentLeftLogicExpressionValue,
      () => this.getCurrentFormulaRoot(),
    )
  }
  enterContainsAny = (ctx: ContainsAnyContext): void => {
    setLogicExpressionCondition(
      ctx,
      this.formula,
      LogicExpressionCondition.CONTAINS_ANY,
      this.columns,
      this.errors,
      this.currentLeftLogicExpressionValue,
      () => this.getCurrentFormulaRoot(),
    )
  }
  enterContainsAll = (ctx: ContainsAllContext): void => {
    setLogicExpressionCondition(
      ctx,
      this.formula,
      LogicExpressionCondition.CONTAINS_ALL,
      this.columns,
      this.errors,
      this.currentLeftLogicExpressionValue,
      () => this.getCurrentFormulaRoot(),
    )
  }
  enterDoesNotContainAny = (ctx: DoesNotContainAnyContext): void => {
    setLogicExpressionCondition(
      ctx,
      this.formula,
      LogicExpressionCondition.DOES_NOT_CONTAIN_ANY,
      this.columns,
      this.errors,
      this.currentLeftLogicExpressionValue,
      () => this.getCurrentFormulaRoot(),
    )
  }
  enterIsEmpty = (ctx: IsEmptyContext): void => {
    setLogicExpressionCondition(
      ctx,
      this.formula,
      LogicExpressionCondition.IS_EMPTY,
      this.columns,
      this.errors,
      this.currentLeftLogicExpressionValue,
      () => this.getCurrentFormulaRoot(),
    )
  }
  enterIsNotEmpty = (ctx: IsNotEmptyContext): void => {
    setLogicExpressionCondition(
      ctx,
      this.formula,
      LogicExpressionCondition.IS_NOT_EMPTY,
      this.columns,
      this.errors,
      this.currentLeftLogicExpressionValue,
      () => this.getCurrentFormulaRoot(),
    )
  }

  // Has terminal (columnId, number, string, boolean)
  enterLogicExprColumnRef = (ctx: LogicExprColumnRefContext): void => {
    if (!ctx.parentCtx) {
      throw new Error('Column ID cannot be root context of formula')
    }
    const fieldId = getTableColumnId(ctx)

    // Look up the field to determine its type
    const field = this.columns.find((col) => col.id === fieldId)

    // Use formInputId for form inputs, columnId for table columns
    const valueInput =
      field?.fieldType === 'formInput' ? { formInputId: fieldId } : { columnId: fieldId }

    addLogicExpressionValue(
      ctx,
      this.formula,
      valueInput,
      this.columns,
      this.errors,
      this.currentLeftLogicExpressionValue,
      () => this.getCurrentFormulaRoot(),
    )
  }
  enterNumericValue = (ctx: NumericValueContext): void => {
    if (!ctx.parentCtx) {
      throw new Error(`Number cannot be root context of formula`)
    }
    const number = parseFloat(ctx.NUMBER().getText())
    addLogicExpressionValue(
      ctx,
      this.formula,
      {
        value: { type: DossDataFormat.NUMBER, data: number },
      },
      this.columns,
      this.errors,
      this.currentLeftLogicExpressionValue,
      () => this.getCurrentFormulaRoot(),
    )
  }
  enterNegativeNumericValue = (ctx: NegativeNumericValueContext): void => {
    if (!ctx.parentCtx) {
      throw new Error(`Negative number cannot be root context of formula`)
    }
    const number = parseFloat(ctx.NUMBER().getText())
    const negativeNumber = number * -1
    addLogicExpressionValue(
      ctx,
      this.formula,
      {
        value: { type: DossDataFormat.NUMBER, data: negativeNumber },
      },
      this.columns,
      this.errors,
      this.currentLeftLogicExpressionValue,
      () => this.getCurrentFormulaRoot(),
    )
  }
  enterStringValue = (ctx: StringValueContext): void => {
    if (!ctx.parentCtx) {
      throw new Error(`String cannot be root context of formula`)
    }
    const string = getStringValue(ctx)
    addLogicExpressionValue(
      ctx,
      this.formula,
      {
        value: { type: DossDataFormat.STRING, data: string },
      },
      this.columns,
      this.errors,
      this.currentLeftLogicExpressionValue,
      () => this.getCurrentFormulaRoot(),
    )
  }
  enterBooleanValue = (ctx: BooleanValueContext): void => {
    if (!ctx.parentCtx) {
      throw new Error('Boolean cannot be root context of formula')
    }
    const booleanText = ctx.BOOLEAN().getText().toLowerCase()
    const boolean = booleanText === 'true'
    addLogicExpressionValue(
      ctx,
      this.formula,
      {
        value: { type: DossDataFormat.BOOLEAN, data: boolean },
      },
      this.columns,
      this.errors,
      this.currentLeftLogicExpressionValue,
      () => this.getCurrentFormulaRoot(),
    )
  }
  // When entering a list, add the logic expression value for type array with an empty array
  enterNumericValueList = (ctx: NumericValueListContext): void => {
    addLogicExpressionValue(
      ctx,
      this.formula,
      {
        value: { type: DossDataFormat.ARRAY, data: [] },
      },
      this.columns,
      this.errors,
      this.currentLeftLogicExpressionValue,
      () => this.getCurrentFormulaRoot(),
    )
  }
  enterStringValueList = (ctx: StringValueListContext): void => {
    addLogicExpressionValue(
      ctx,
      this.formula,
      {
        value: { type: DossDataFormat.ARRAY, data: [] },
      },
      this.columns,
      this.errors,
      this.currentLeftLogicExpressionValue,
      () => this.getCurrentFormulaRoot(),
    )
  }
  enterNumericItem = (ctx: NumericItemContext): void => {
    if (!ctx.parentCtx) {
      throw new Error('Number item cannot be root context of formula')
    }

    const number = parseFloat(ctx.NUMBER().getText())
    addValueToLogicExpressionValue(ctx.parentCtx, this.formula, number)
  }
  enterNegativeNumericItem = (ctx: NegativeNumericItemContext): void => {
    if (!ctx.parentCtx) {
      throw new Error('Negative number item cannot be root context of formula')
    }

    const number = parseFloat(ctx.NUMBER().getText())
    const negativeNumber = number * -1
    addValueToLogicExpressionValue(ctx.parentCtx, this.formula, negativeNumber)
  }
  enterStringItem = (ctx: StringItemContext): void => {
    if (!ctx.parentCtx) {
      throw new Error('String item cannot be root context of formula')
    }

    const string = getStringValue(ctx)
    addValueToLogicExpressionValue(ctx.parentCtx, this.formula, string)
  }

  // Get the constructed formula
  getCurrentObject(): ExtendedFormulaInput {
    return this.formula
  }
}

/**
 * Parse the input text and return tokens
 * @param text - The text to parse
 * @param columns - The columns available for reference
 * @param languageScope - The language scope to use ('formula' or 'logicalExpression'). Defaults to 'formula'.
 */
export function parseText(
  text: string,
  columns: ParseableColumn[],
  languageScope: 'formula',
  options?: ParseFormulaOptions,
): FormulaParseResults
export function parseText(
  text: string,
  columns: ParseableColumn[],
  languageScope: 'logicalExpression',
  options?: ParseFormulaOptions,
): LogicExpressionParseResults
export function parseText(
  text: string,
  columns: ParseableColumn[],
  languageScope: LanguageScope,
  options?: ParseFormulaOptions,
): ParseResults
export function parseText(
  text: string,
  columns: ParseableColumn[],
  languageScope: LanguageScope = 'formula',
  options: ParseFormulaOptions = {},
): ParseResults {
  const lexer = new FormulaLexer(CharStreams.fromString(text))
  const tokensStream = new CommonTokenStream(lexer)

  const parser = new formulaParser(tokensStream)

  // Remove the default error listeners to stop console.errors
  const errorListener = new FormulaErrorListener()
  parser.removeErrorListeners()
  lexer.removeErrorListeners()
  // Add our custom error listener to the parser
  parser.addErrorListener(errorListener)
  parser.buildParseTrees = true

  // Use the appropriate parser entry point based on languageScope
  const parseTree = languageScope === 'logicalExpression' ? parser.logicExpr() : parser.expr()

  const modelBuilder = new FormulaModelBuilder(columns, options)
  ParseTreeWalker.DEFAULT.walk(modelBuilder, parseTree)
  const formula = modelBuilder.getCurrentObject()

  tokensStream.fill() // Load all tokens into the stream

  // Retrieve all tokens as CommonToken instances and filter out EOF
  const tokens: CommonToken[] = []
  let token: CommonToken
  for (let i = 0; i < tokensStream.size; i++) {
    token = tokensStream.get(i) as CommonToken
    if (token.type !== FormulaLexer.EOF) {
      tokens.push(token)
    }
  }

  // Get errors from error listener and formula builder
  const errors = [...errorListener.errors, ...modelBuilder.getErrors()]

  // Type-check formula sub-expressions once the input is syntactically valid — in 'formula'
  // scope the whole tree is a formula; in 'logicalExpression' scope this validates formulas
  // nested inside conditions.
  if (errorListener.errors.length === 0) {
    const typeCheckResults = typeCheckFormulaTree(parseTree, columns)
    errors.push(...typeCheckResults.errors)
    // Resolve and stamp the output type onto every formula node of the built model
    inferOutputTypes(formula, columns)
  }

  // When parsing logic expressions at the root level, extract the logic expression
  // from the formula's columnOperationFilter
  if (languageScope === 'logicalExpression') {
    const logicExpression = formula.columnOperationFilter || null
    const logicExpressionInput = logicExpression
      ? removeInvokingStatesFromLogicExpression(logicExpression)
      : null

    return {
      tokens,
      errors,
      tree: parseTree,
      formula: logicExpressionInput,
    } as ParseResults
  }

  const formulaInput = removeInvokingStates(formula)

  return {
    tokens,
    errors,
    tree: parseTree,
    formula: formulaInput,
  } as ParseResults
}

/**
 * Get the display for the table column. If the table column is on the same table as where
 * the formula is being made, just return the column name.  If the table column is on a different
 * table, include that table name and the table column name. If the table is a child table, also
 * include the parent table name
 */
export function getTableColumnDisplay(tableColumn: FormulaDisplayColumn, tableId?: string): string {
  if (tableId === tableColumn.table.id) {
    return tableColumn.name
  }
  const tableDisplay = getTableDisplay(tableColumn)
  return `${tableDisplay}:${tableColumn.name}`
}

/**
 * Get the table display for a table column.  If the table column is on a child table, include
 * the name of the parent table in the display, otherwise, just include the name of the column's
 * table
 */
export function getTableDisplay(tableColumn: FormulaDisplayColumn): string {
  if (tableColumn.table.parentTable) {
    return `${tableColumn.table.parentTable.name} > ${tableColumn.table.name}`
  }
  return tableColumn.table.name
}

/**
 * Resolves and stamps outputType on every formula node of the built model.
 */
function inferOutputTypes(formula: ExtendedFormulaInput, columns: ParseableColumn[]): void {
  const columnsById = new Map(columns.map((column) => [column.id, column]))
  const resolveRef = (referenceId: string): FormulaValueType | undefined => {
    const column = columnsById.get(referenceId)
    return column
      ? dataTypeToFormulaValueType(column.type, column.lookupColumn?.type ?? null)
      : undefined
  }

  const stampFormulaTree = (node: ExtendedFormulaInput): void => {
    // Stamps the output type onto every formula node of the built model
    inferFormulaOutputType(node, resolveRef, [], { assignOutputTypes: true })
    // Stamp the output type onto formulas embedded in logic expressions.
    stampEmbeddedLogicFormulas(node)
  }

  const stampEmbeddedLogicFormulas = (node: ExtendedFormulaInput): void => {
    if (node.columnOperationFilter) {
      stampLogicExpression(node.columnOperationFilter)
    }
    for (const value of node.formulaValues) {
      if (value.formula) {
        stampEmbeddedLogicFormulas(value.formula)
      }
      for (const condition of value.conditions) {
        stampLogicExpression(condition.condition)
        stampEmbeddedLogicFormulas(condition.formula)
      }
    }
  }

  const stampLogicExpression = (expression: ExtendedLogicExpressionInput): void => {
    for (const value of expression.values) {
      if (value.formula) {
        stampFormulaTree(value.formula)
      }
      if (value.nestedExpression) {
        stampLogicExpression(value.nestedExpression)
      }
    }
  }

  stampFormulaTree(formula)
}

/**
 * Recursively removes the invokingStates property from FormulaInput and its nested objects.
 */
function removeInvokingStates(formula: ExtendedFormulaInput): FormulaInput {
  const result: FormulaInput = {
    ...omitInvokingStates(formula),
    formulaValues: formula.formulaValues.map(removeInvokingStatesFromFormulaValue),
  }
  if (formula.columnOperationFilter) {
    result.columnOperationFilter = removeInvokingStatesFromLogicExpression(
      formula.columnOperationFilter,
    )
  }
  return result
}

/**
 * Recursively removes the invokingStates property from FormulaValueInput and its nested objects.
 */
function removeInvokingStatesFromFormulaValue(
  formulaValue: ExtendedFormulaValueInput,
): FormulaValueInput {
  const result: FormulaValueInput = {
    ...omitInvokingStates(formulaValue),
    conditions: formulaValue.conditions.map((condition) => ({
      ...condition,
      condition: removeInvokingStatesFromLogicExpression(condition.condition),
      formula: removeInvokingStates(condition.formula),
    })),
  }
  if (formulaValue.formula) {
    result.formula = removeInvokingStates(formulaValue.formula)
  }
  return result
}

/**
 * Recursively removes the invokingStates property from LogicExpressionInput and its nested objects.
 */
function removeInvokingStatesFromLogicExpression(
  logicExpression: ExtendedLogicExpressionInput,
): LogicExpressionInput {
  return {
    ...omitInvokingStates(logicExpression),
    values: logicExpression.values.map((value) => {
      const result: LogicExpressionValueInput = omitInvokingStates(value)
      if (value.nestedExpression) {
        result.nestedExpression = removeInvokingStatesFromLogicExpression(value.nestedExpression)
      }
      if (value.formula) {
        result.formula = removeInvokingStates(value.formula)
      }
      return result
    }),
  }
}

/**
 * Helper function to omit the invokingStates property from an object.
 */
function omitInvokingStates<T extends { invokingStates: string[] }>(
  obj: T,
): Omit<T, 'invokingStates'> {
  return _.omit(obj, 'invokingStates')
}

/**
 * Returns the content of a column id token without the surrounding curly braces
 */
export function getTableColumnId(ctx: LogicExprColumnRefContext | ColumnRefContext): string {
  const rawTableColumnId = ctx.COLUMN_ID().getText()
  const tableColumnId = rawTableColumnId.replace(/[{}]/g, '')
  return tableColumnId
}

/**
 * Returns the content of a string token without the double quotation marks
 *  that surround it
 */
function getStringValue(ctx: StringValueContext | StringLiteralContext): string {
  const rawString = ctx.STRING().getText()
  const cleanedString = rawString.replace(/^"(.*)"$/, '$1')
  return cleanedString
}

/**
 * Generate an error token for a particular context
 */
export const createErrorToken = (ctx: ParserRuleContext, message: string): ErrorToken => {
  return {
    start: ctx.start.start,
    end: ctx.stop ? ctx.stop.stop + 1 : ctx.start.stop + 1,
    message: message,
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    recognizer: ctx.parser!,
    line: ctx.start.line,
    charPositionInLine: ctx.start.column,
    e: ctx.exception,
    offendingSymbol: ctx.start,
    isBuilderError: true,
  }
}

/**
 * Helper to resolve DossDataFormat from a column/field reference.
 * Handles both columnId (table columns) and formInputId (form inputs).
 */
const resolveFieldType = (
  fieldId: string | null | undefined,
  columns: ParseableColumn[],
): { type: DossDataFormat | undefined; isColumn: boolean } => {
  if (!fieldId) {
    return { type: undefined, isColumn: false }
  }

  const field = columns.find((col) => col.id === fieldId)
  if (!field) {
    return { type: undefined, isColumn: false }
  }

  const baseType = dataTypeToDossDataFormat(field.type)

  // For Lookup types, use the type of the lookup column
  if (baseType === DossDataFormat.LOOKUP && field.lookupColumn) {
    return {
      type: dataTypeToDossDataFormat(field.lookupColumn.type),
      isColumn: field.fieldType === 'tableColumn',
    }
  }

  return { type: baseType, isColumn: field.fieldType === 'tableColumn' }
}

/**
 * Resolves one comparison operand to the shape the operand rules take.
 * Handles literal values, columnId, and formInputId. Returns undefined when the comparison has
 * no value in that position, which is what the IS_EMPTY / IS_NOT_EMPTY rules look at.
 */
const resolveLogicOperand = (
  value: ExtendedLogicExpressionValueInput | undefined,
  columns: ParseableColumn[],
): LogicOperandType | undefined => {
  if (!value) {
    return undefined
  }
  if (value.value && isTableCellData(value.value)) {
    return { type: value.value.type, isColumn: false }
  }
  return resolveFieldType(value.columnId || value.formInputId, columns)
}

/**
 * Checks that the logic expression is valid.
 *
 * The operand rules themselves live in `checkLogicExpressionOperandTypes` so the write-path
 * validator can apply the same set to a structured formula that was never parsed from a string.
 * This function is the editor's half: it maps each violation onto the token the editor should
 * underline.
 */
const checkValidLogicExpression = (
  ctx: ParserRuleContext,
  expression: ExtendedLogicExpressionInput,
  columns: ParseableColumn[],
  errors: ErrorToken[],
  expressionValueOrder: number,
  leftValueCtx: ParserRuleContext | null,
): void => {
  if (expression.condition) {
    const leftValue = expression.values.find((value) => value.order === 1)
    const rightValue = expression.values.find((value) => value.order === 2)

    // Should only set the error for the left value if not currently evaluating the right value
    // and there is a leftValueCtx
    const shouldSetErrorForLeftValue = expressionValueOrder !== 2 && leftValueCtx
    const shouldSetErrorForRightValue = expressionValueOrder === 2

    const violations = checkLogicExpressionOperandTypes(
      expression.condition,
      resolveLogicOperand(leftValue, columns),
      resolveLogicOperand(rightValue, columns),
    )

    for (const violation of violations) {
      if (violation.side === 'left') {
        if (shouldSetErrorForLeftValue) {
          errors.push(createErrorToken(leftValueCtx, violation.message))
        }
      } else if (violation.side === 'right') {
        if (shouldSetErrorForRightValue) {
          errors.push(createErrorToken(ctx, violation.message))
        }
      } else {
        errors.push(createErrorToken(ctx, violation.message))
      }
    }
  }
}
